import React, { useState, useCallback } from 'react';
import { Monitor } from 'lucide-react';
import MachineSelector from './MachineSelector';
import LauncherGrid from './LauncherGrid';
import ProfileDetailsModal from './ProfileDetailsModal';
import { ToastContainer, useToast } from './Toast';
import { tasksApi } from '../../../../utils/api/tasks';

/**
 * LauncherSpace - Main container for the launcher space
 * 
 * Features:
 * - MachineSelector dropdown for target selection
 * - LauncherGrid showing available DCC apps filtered by machine capabilities
 * - Profile selection and launch via task API
 * - Profile details modal
 * - Toast notifications for launch feedback
 * 
 * Config options (from space.config):
 * - show_offline_machines: boolean - whether to show offline machines
 * - auto_select_current: boolean - auto-select current machine if detected
 */
const LauncherSpace = ({ space, theme = 'dark' }) => {
  const { name, description } = space;
  const [selectedMachine, setSelectedMachine] = useState(null);
  
  // Toast notifications
  const { toasts, toast, removeToast } = useToast();
  
  // Modal state for profile details
  const [modalState, setModalState] = useState({
    isOpen: false,
    launcher: null,
    profile: null,
    capability: null
  });

  // Poll for task completion and update toast
  const pollTaskStatus = useCallback(async (taskId, toastId, profileName) => {
    const maxAttempts = 30; // 15 seconds max
    const pollInterval = 500;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const task = await tasksApi.getTask(taskId);
        console.log(`Task ${taskId} status:`, task.status, task);
        
        if (task.status === 'completed') {
          toast.update(toastId, `✓ Launched ${profileName}`, 'success', 4000);
          return;
        } else if (task.status === 'failed') {
          const errorMsg = task.error_message || 'Launch failed';
          toast.update(toastId, `✗ ${errorMsg}`, 'error', 8000);
          return;
        }
        // Still running/queued, keep polling
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (err) {
        console.error('Poll error:', err);
        // Don't fail immediately, might be temporary
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }
    // Timeout - leave as warning
    toast.update(toastId, `⏳ ${profileName} - still processing...`, 'warning', 6000);
  }, [toast]);

  // Handle launch action (with optional advanced options)
  const handleLaunch = useCallback(async (launcher, profile, options = {}) => {
    if (!selectedMachine) {
      toast.error('Please select a target machine first');
      return;
    }

    const { additionalArgs, environmentOverrides } = options;
    const hasAdvancedOptions = additionalArgs?.length > 0 || 
      (environmentOverrides && Object.keys(environmentOverrides).length > 0);

    // Show yellow "sending" toast immediately
    const advancedNote = hasAdvancedOptions ? ' +opts' : '';
    const toastId = toast.warning(
      `Sending: ${profile.name} → ${selectedMachine.hostname}${advancedNote}`,
      15000 // Long duration, will be updated
    );

    try {
      console.log('Launching:', {
        launcher: launcher.name,
        profile: profile.name,
        machine: selectedMachine.hostname,
        additionalArgs,
        environmentOverrides
      });

      const response = await tasksApi.launch({
        launcherName: launcher.name,
        profileName: profile.name,
        machineHostname: selectedMachine.hostname,
        additionalArgs: additionalArgs || null,
        environmentOverrides: environmentOverrides || null
      });

      console.log('Launch response:', response);
      
      // Start polling for result
      if (response.task_id) {
        pollTaskStatus(response.task_id, toastId, profile.name);
      } else {
        // No task ID returned, assume success
        toast.update(toastId, `Sent: ${profile.name}`, 'info', 3000);
      }
    } catch (error) {
      console.error('Launch failed:', error);
      
      // Extract error message
      let errorMessage = 'Failed to send launch request';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.detail) {
        errorMessage = error.detail;
      }
      
      toast.update(toastId, `✗ ${errorMessage}`, 'error', 8000);
    }
  }, [selectedMachine, toast, pollTaskStatus]);

  // Handle show details action
  const handleShowDetails = useCallback((launcher, profile, capability) => {
    setModalState({
      isOpen: true,
      launcher,
      profile,
      capability
    });
  }, []);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setModalState({
      isOpen: false,
      launcher: null,
      profile: null,
      capability: null
    });
  }, []);

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 flex-shrink-0">
        <div className="p-3 rounded-lg bg-blue-500/20">
          <Monitor size={24} className="text-blue-400" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-white">{name}</h2>
          {description && (
            <p className="text-sm text-gray-400">{description}</p>
          )}
        </div>
      </div>

      {/* Machine Selector */}
      <div className="mb-6 flex-shrink-0">
        <label className="block text-sm font-medium text-gray-400 mb-2">
          Target Machine
        </label>
        <MachineSelector
          selectedMachine={selectedMachine}
          onSelect={setSelectedMachine}
          className="max-w-md"
        />
      </div>

      {/* Launcher Grid - scrollable area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <LauncherGrid
          machine={selectedMachine}
          spaceId={space.space_id}
          onLaunch={handleLaunch}
          onShowDetails={handleShowDetails}
        />
      </div>

      {/* Profile Details Modal */}
      <ProfileDetailsModal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        profile={modalState.profile}
        launcher={modalState.launcher}
        machineCapability={modalState.capability}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default LauncherSpace;
